import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import {
	InspectionTemplate,
	InspectionTemplateColumn,
	InspectionTemplateDraft,
} from "@/shared/models/Inspection";
import {
	deleteInspectionTemplate,
	upsertInspectionTemplate,
} from "@/shared/repositories/InspectionTemplateRepository";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, Grid2, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useInspectionTemplateCatalog } from "../hooks/useInspectionTemplateCatalog";
import {
	asNewColumns,
	asNewRevisionOf,
	columnsOfTemplate,
	draftHasChanged,
	nextEffectiveFrom,
	sectionRevisions,
} from "../utils/templateDrafts";
import { describeIssue } from "../utils/templateMessages";
import { TemplateModalTarget } from "../utils/templateModal";
import { validateDraft } from "../utils/templateValidation";
import { CloneSectionDialog } from "./CloneSectionDialog";
import { RevisionPromptDialog } from "./RevisionPromptDialog";
import { TemplateColumnsEditor } from "./TemplateColumnsEditor";
import { TemplateDrawingField } from "./TemplateDrawingField";

const moment = getMomentTz();

type Props = {
	target: TemplateModalTarget;
	onHide: () => void;
	onSaved: (message: string) => void;
	onError: (message: string) => void;
};

export const TemplateDraftForm = ({
	target,
	onHide,
	onSaved,
	onError,
}: Props) => {
	const { mode, draft, sectionName, inspectionCount } = target;

	const queryClient = useQueryClient();
	const { data } = useInspectionTemplateCatalog();

	const [form, setForm] = useState<InspectionTemplateDraft>(draft);
	const [drawingName, setDrawingName] = useState<string | null>(null);
	const [isAskingRevision, setIsAskingRevision] = useState(false);
	const [isCloning, setIsCloning] = useState(false);

	const templates = data?.templates ?? [];
	const columns = data?.columns ?? [];
	const sections = data?.sections ?? [];

	const isDelete = mode === "delete";
	const isView = mode === "view";
	const isFrozen = isDelete || isView;
	const isSectionFixed = mode !== "create" && mode !== "duplicate";

	const revisions = sectionRevisions(templates, form.SectionId);
	const issues = validateDraft(revisions, form, draft);

	const isBlankSection =
		(mode === "create" || mode === "revision") &&
		form.SectionId !== null &&
		revisions.latest === null;

	const targetSectionName =
		sections.find((section) => section.SectionId === form.SectionId)
			?.SectionName ||
		sectionName ||
		"this section";

	const pickSection = (sectionId: number | null) => {
		if (sectionId === null) {
			setForm((current) => ({ ...current, SectionId: null, Rev: null }));
			return;
		}

		const picked = sectionRevisions(templates, sectionId);
		const latest = picked.latest;
		const carriesSheet = mode === "create" && latest !== null;

		setForm((current) => ({
			...current,
			SectionId: sectionId,
			Rev: picked.nextRev,
			EffectiveFrom: nextEffectiveFrom(
				latest?.EffectiveFrom ?? null,
				new Date(),
			),
			UploadFileId: carriesSheet ? latest.UploadFileId : current.UploadFileId,
			Columns: carriesSheet
				? asNewColumns(columnsOfTemplate(columns, latest))
				: current.Columns,
		}));

		if (carriesSheet) setDrawingName(latest.DrawingFileName);
	};

	const cloneFrom = (template: InspectionTemplate, withDrawing: boolean) => {
		setForm((current) => ({
			...current,
			UploadFileId: withDrawing ? template.UploadFileId : null,
			Columns: asNewColumns(columnsOfTemplate(columns, template)),
		}));
		setDrawingName(withDrawing ? template.DrawingFileName : null);
		setIsCloning(false);
	};

	const onSettled = async () => {
		await queryClient.invalidateQueries({ queryKey: ["inspection-templates"] });
		onSaved(`The template was ${isDelete ? "deleted" : "saved"} successfully`);
	};

	const upsertMutation = useMutation(upsertInspectionTemplate, {
		onSuccess: onSettled,
		onError: (mutationError: Error) => onError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteInspectionTemplate, {
		onSuccess: onSettled,
		onError: (mutationError: Error) => onError(mutationError.message),
	});

	const isEditingExisting = mode === "update" && form.TemplateId !== null;

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		if (isView) return;

		if (isDelete) {
			deleteMutation.mutate(form.TemplateId);
			return;
		}

		if (isEditingExisting && draftHasChanged(form, draft)) {
			setIsAskingRevision(true);
			return;
		}

		upsertMutation.mutate(form);
	};

	const previous = revisions.previousTo(revisions.latest);

	return (
		<>
			<form
				onSubmit={onSubmit}
				style={{
					display: "flex",
					flexDirection: "column",
					flex: "1 1 auto",
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				<MesfModal.Content
					style={{ padding: "15px 30px", overflowY: "auto", minHeight: 0 }}
					dividers
				>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 2, xs: 12 }}>
							<SectionSelector
								value={form.SectionId}
								size="small"
								required
								assetId={MILL_1.id}
								disabled={isSectionFixed}
								onChange={pickSection}
							/>
						</Grid2>
						<Grid2 size={{ md: 1.5, xs: 12 }}>
							<TextField
								label="Rev"
								type="number"
								required
								value={form.Rev ?? ""}
								disabled={isFrozen}
								error={issues.rev !== null}
								fullWidth
								size="small"
								autoComplete="off"
								slotProps={{ htmlInput: { min: 0, step: 1 } }}
								onChange={(event) => {
									const rev = Number.parseInt(event.target.value, 10);

									setForm((current) => ({
										...current,
										Rev: Number.isNaN(rev) || rev < 0 ? null : rev,
									}));
								}}
							/>
						</Grid2>
						<Grid2 size={{ md: 2.5, xs: 12 }}>
							<DatePicker
								label="Effective From"
								value={form.EffectiveFrom ? moment(form.EffectiveFrom) : null}
								disabled={isFrozen}
								format="MM/DD/YYYY"
								onChange={(value) =>
									setForm((current) => ({
										...current,
										EffectiveFrom: value ? value.toDate() : null,
									}))
								}
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
										error: issues.effectiveFrom !== null,
									},
								}}
							/>
						</Grid2>
						<Grid2 size={{ md: 6, xs: 12 }}>
							<TemplateDrawingField
								uploadFileId={form.UploadFileId}
								fileName={drawingName}
								templates={templates}
								sectionName={targetSectionName}
								disabled={isFrozen}
								onChange={(uploadFileId, fileName) => {
									setForm((current) => ({
										...current,
										UploadFileId: uploadFileId,
									}));
									setDrawingName(fileName);
								}}
								onError={onError}
							/>
						</Grid2>

						{!isFrozen && issues.rev !== null && (
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="caption" color="error">
									{describeIssue(issues.rev)}
								</Typography>
							</Grid2>
						)}

						{!isFrozen && issues.effectiveFrom !== null && (
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="caption" color="error">
									{describeIssue(issues.effectiveFrom)}
								</Typography>
							</Grid2>
						)}

						{!isFrozen && form.SectionId !== null && (
							<Grid2
								size={{ xs: 12 }}
								container
								alignItems="center"
								spacing={1}
							>
								<Button
									variant="outlined"
									size="small"
									startIcon={<ContentCopyIcon />}
									onClick={() => setIsCloning(true)}
								>
									Clone from another section
								</Button>
								{isBlankSection && (
									<Typography variant="caption" color="text.secondary">
										{`${targetSectionName} has no sheet yet: write its columns below, or start from the sheet of a section that is already inspected.`}
									</Typography>
								)}
							</Grid2>
						)}

						{!isDelete && (
							<Grid2 size={{ xs: 12 }}>
								<TemplateColumnsEditor
									columns={form.Columns}
									disabled={isView}
									onChange={(nextColumns: InspectionTemplateColumn[]) =>
										setForm((current) => ({ ...current, Columns: nextColumns }))
									}
								/>
							</Grid2>
						)}
						{isDelete && (
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="body2">
									{`Rev ${revisions.latest?.Rev ?? form.Rev} is the last revision written for ${sectionName} and is the one that will be deleted: the history of a sheet is unwound from its end. ${
										previous === null
											? "The section is left with no template to inspect under."
											: `${sectionName} goes back to Rev ${previous.Rev}.`
									} If an inspection was already taken under it, it is retired instead of removed.`}
								</Typography>
							</Grid2>
						)}
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					{isView ? (
						<Grid2 container justifyContent="flex-end">
							<Button variant="contained" color="inherit" onClick={onHide}>
								Close
							</Button>
						</Grid2>
					) : (
						<FooterModalButtons
							isSubmitLoading={
								upsertMutation.isLoading || deleteMutation.isLoading
							}
							onHide={onHide}
							buttonLabel={isDelete ? "Delete" : "Save"}
							buttonColor={isDelete ? "secondary" : "primary"}
							disabled={
								!isDelete &&
								(form.SectionId === null ||
									form.Rev === null ||
									issues.rev !== null ||
									issues.effectiveFrom !== null)
							}
						/>
					)}
				</MesfModal.Actions>
			</form>
			{isAskingRevision && (
				<RevisionPromptDialog
					revisions={revisions}
					currentRev={draft.Rev}
					sectionName={sectionName}
					inspectionCount={inspectionCount}
					onClose={() => setIsAskingRevision(false)}
					onUpdateThisRevision={() => {
						setIsAskingRevision(false);
						upsertMutation.mutate(form);
					}}
					onCreateRevision={(rev) => {
						upsertMutation.mutate(
							asNewRevisionOf(
								form,
								rev,
								revisions.latest?.EffectiveFrom ?? null,
								new Date(),
							),
						);
						setIsAskingRevision(false);
					}}
				/>
			)}
			{isCloning && (
				<CloneSectionDialog
					templates={templates}
					sectionId={form.SectionId}
					sectionName={targetSectionName}
					rev={form.Rev}
					onClose={() => setIsCloning(false)}
					onClone={cloneFrom}
				/>
			)}
		</>
	);
};
